import { z } from 'zod';
import { isValidBin } from './bin-checksum';
import { businessCategoryIds, cityIds } from './dictionaries';
import { normalizeKzPhone } from './phone';

/**
 * Client validation schema for the PromoLanding business application. It mirrors
 * the backend contract for UX only — server-side validation
 * stays authoritative. All string limits are evaluated AFTER Unicode
 * normalization (NFC) and trim.
 */

export const businessTypes = ['legal_entity', 'individual_entrepreneur'] as const;

const NAME_MIN = 2;
const NAME_MAX = 150;
const EMAIL_MIN_OCTETS = 3;
const EMAIL_MAX_OCTETS = 254;
const DESCRIPTION_MAX = 500;

/** NFC-normalize and trim a string; pass non-strings through for zod to reject. */
const normalizeTrim = (value: unknown): unknown =>
  typeof value === 'string' ? value.normalize('NFC').trim() : value;

/** UTF-8 octet length, used for the RFC 5321 email envelope limit. */
const octetLength = (value: string): number => new TextEncoder().encode(value).length;

/** Lowercase only the domain part; the local part is case-sensitive per RFC. */
const lowercaseEmailDomain = (value: string): string => {
  const at = value.lastIndexOf('@');
  if (at === -1) return value;
  return `${value.slice(0, at)}@${value.slice(at + 1).toLowerCase()}`;
};

const nameField = z.preprocess(
  normalizeTrim,
  z.string().min(NAME_MIN, 'applyForm.errors.required').max(NAME_MAX, 'applyForm.errors.tooLong'),
);

export const applySchema = z.object({
  contactName: nameField,
  businessName: nameField,
  phone: z
    .string()
    .transform((value) => normalizeKzPhone(value))
    .refine((value): value is string => value !== null, 'applyForm.errors.phone'),
  email: z.preprocess(
    (value) => (typeof value === 'string' ? value.trim() : value),
    z
      .string()
      .min(1, 'applyForm.errors.required')
      .email('applyForm.errors.email')
      .refine(
        (value) => octetLength(value) >= EMAIL_MIN_OCTETS && octetLength(value) <= EMAIL_MAX_OCTETS,
        'applyForm.errors.emailLength',
      )
      .transform(lowercaseEmailDomain),
  ),
  businessType: z.enum(businessTypes),
  bin: z.preprocess(
    (value) => (typeof value === 'string' ? value.replace(/\D/g, '') : value),
    z.string().refine(isValidBin, 'applyForm.errors.bin'),
  ),
  // A plain string on input so the empty default (unselected) is valid form
  // state; the pipe rejects it as "required" and enforces the dictionary.
  category: z
    .string()
    .min(1, 'applyForm.errors.category')
    .pipe(
      z.enum(businessCategoryIds, {
        errorMap: () => ({ message: 'applyForm.errors.category' }),
      }),
    ),
  locationsCount: z.preprocess(
    (value) => (typeof value === 'string' ? value.trim() : value),
    z
      .string()
      .min(1, 'applyForm.errors.required')
      .transform((value) => Number(value))
      .refine((value) => Number.isInteger(value) && value >= 1, 'applyForm.errors.locationsCount'),
  ),
  cities: z.enum(cityIds, {
    errorMap: () => ({ message: 'applyForm.errors.city' }),
  }),
  description: z.preprocess(
    normalizeTrim,
    z.string().max(DESCRIPTION_MAX, 'applyForm.errors.descriptionTooLong').optional(),
  ),
  consent: z.boolean().refine((value) => value === true, 'applyForm.errors.consent'),
});

export type ApplyFormValues = z.input<typeof applySchema>;
export type ApplyFormPayload = z.output<typeof applySchema>;
